import type { SeoLandingConfig } from "../types";

// Fatos com fonte em .claude/skills/seo/reference/fidexa-empresa.md
// (siglas: PEGN, ERP = Portal ERP, NV = Novo Varejo). O que a página
// /integracoes publica sobre o Linx vem de landingPage/constants/integrations.ts.
// Sem detalhe técnico da integração (endpoint, módulo, tempo de sincronização):
// nada disso está na base.
const SOURCE_ERP_NV = "Portal ERP e Novo Varejo, ago/2026";
const SOURCE_PEGN = "Pequenas Empresas & Grandes Negócios, ago/2026";

export const integracaoLinx: SeoLandingConfig = {
  path: "/integracoes/linx",
  kind: "ferramenta",
  keyword: "integração Linx para cobrança de crediário",
  title: "Integração Linx para cobrança de crediário",
  description:
    "A Fidexa lê a carteira do crediário direto do TOTVS Linx, cobra no WhatsApp e recebe a parcela por Pix com baixa automática, sem trocar o sistema da loja.",
  breadcrumbLabel: "Linx",
  breadcrumbParent: { name: "Integrações", path: "/integracoes" },
  updatedAt: "2026-10-17",
  service: {
    name: "Integração da Fidexa com o TOTVS Linx",
    serviceType:
      "Cobrança de crediário integrada ao ERP TOTVS Linx, com Pix e baixa automática",
    audience: "Lojas e redes do varejo que usam o ERP TOTVS Linx",
  },
  sections: [
    {
      type: "hero",
      eyebrow: "Integração com o TOTVS Linx",
      title: "Integração Linx para cobrança de crediário com baixa automática",
      lead: "Os clientes, as parcelas e os vencimentos saem do seu ERP Linx e viram cobrança no WhatsApp. Quando o cliente paga por Pix, a parcela já sai baixada.",
      ctaLabel: "Agendar demonstração gratuita",
      secondaryLabel: "Ver a integração por dentro",
      secondaryHref: "#como-funciona",
      reassurance:
        "Você continua no Linx. Demonstração gratuita com um especialista.",
    },
    {
      type: "proof",
      title: "A Fidexa atende 249 empresas do varejo. Algumas delas:",
      clientNames: [
        "Lojas Rosário",
        "Grupo Minas",
        "SuperModas",
        "Lojas Skala",
        "Sete 4 Sete",
        "Lojas Elder",
      ],
    },
    {
      type: "shortAnswer",
      id: "o-que-e",
      title: "O que é a integração da Fidexa com o Linx",
      answer:
        "É a conexão entre o ERP TOTVS Linx e a Fidexa: a carteira do crediário próprio chega à plataforma sem ninguém exportar arquivo, a régua automática avisa cada vencimento no WhatsApp e o Fidexa Pay recebe a parcela por Pix e dá a baixa. A loja não precisa trocar de sistema de gestão.",
      bullets: [
        "O Fidexa Pay nasceu de uma parceria com a Totvs Linx.",
        "No autoatendimento, o cliente paga sozinho a qualquer hora.",
        "Também recebe parcelas futuras, não só as atrasadas.",
      ],
    },
    {
      type: "steps",
      id: "como-funciona",
      title: "Do Linx ao Pix: o caminho de cada parcela",
      lead: "Quatro etapas, sem planilha no meio.",
      steps: [
        {
          title: "O Linx entrega a carteira",
          text: "Clientes, parcelas e vencimentos vêm direto do ERP para a Fidexa. Ninguém precisa gerar e enviar relatório.",
        },
        {
          title: "Cada vencimento vira mensagem",
          text: "A partir das datas que estão no Linx, o cliente recebe o aviso preventivo e, se atrasar, a cobrança do débito.",
        },
        {
          title: "A loja negocia no chat",
          text: "Quando o cliente responde para pedir prazo ou acordo, a conversa cai no chat da Fidexa e a equipe da loja combina as condições.",
        },
        {
          title: "Pix pago, parcela baixada",
          text: "Quem deve seleciona a parcela e quita por Pix sem sair do WhatsApp. O pagamento é computado e a parcela, baixada pela Fidexa.",
        },
      ],
    },
    {
      type: "comparison",
      id: "comparativo",
      title: "Cobrar fora do sistema ou com a Fidexa ligada ao Linx",
      lead: "O que sai da rotina do financeiro quando o ERP conversa com a cobrança.",
      caption:
        "Comparação entre cobrar o crediário fora do sistema, exportando dados do Linx, e cobrar com a Fidexa integrada ao TOTVS Linx",
      columns: [
        { key: "manual", label: "Cobrança fora do sistema" },
        { key: "fidexa", label: "Fidexa integrada ao Linx", highlight: true },
      ],
      rows: [
        {
          label: "Exportar a carteira do Linx para saber quem cobrar",
          cells: {
            manual: "A cada rodada de cobrança",
            fidexa: "Não precisa: os dados vêm do ERP",
          },
        },
        {
          label: "Aviso antes do vencimento",
          cells: {
            manual: "Depende de alguém da loja",
            fidexa: "Sai pela régua no WhatsApp",
          },
        },
        {
          label: "Negociação do atraso",
          cells: {
            manual: "Por telefone, quando sobra tempo",
            fidexa: "Equipe da unidade, pelo chat unificado",
          },
        },
        {
          label: "Pagamento da parcela",
          cells: {
            manual: "Fora da conversa",
            fidexa: "Pix dentro do WhatsApp",
          },
        },
        {
          label: "Conferir o pagamento e dar baixa",
          cells: {
            manual: "Na mão, no extrato e no sistema",
            fidexa: "A Fidexa dá a baixa",
          },
        },
        {
          label: "Carteira de várias lojas",
          cells: {
            manual: "Cada loja cuida da sua",
            fidexa: "Centralizada num dashboard único",
          },
        },
        {
          label: "Pagar à noite e no fim de semana",
          cells: {
            manual: false,
            fidexa: "Sim: o Fidexa Pay fica aberto 24 horas",
          },
        },
      ],
    },
    {
      type: "metrics",
      id: "numeros",
      title: "O que a cobrança da Fidexa já devolveu ao varejo",
      metrics: [
        {
          value: "R$ 39,34 mi",
          label: "Recuperados para o varejo desde o início da operação",
          source: SOURCE_ERP_NV,
        },
        {
          value: "R$ 6 mi",
          label: "Retomados por mês, aproximadamente",
          source: SOURCE_PEGN,
        },
        {
          value: "32,10%",
          label: "Recuperação média da carteira",
          source: "Plataforma Fidexa",
        },
      ],
    },
    {
      type: "faq",
      id: "perguntas",
      title: "Perguntas frequentes sobre a integração com o Linx",
      items: [
        {
          question: "A Fidexa tem integração com o Linx?",
          answer:
            "Sim. A Fidexa se integra ao ERP TOTVS Linx, que aparece na página de integrações como Linx Seta Digital. A conexão é por API, e a importação por planilha também é aceita.",
        },
        {
          question: "O que muda na cobrança com o Linx integrado?",
          answer:
            "Ninguém precisa exportar arquivo para cobrar. Clientes, parcelas e vencimentos chegam do ERP à Fidexa, que dispara pelo WhatsApp o aviso antes do vencimento e a cobrança do atraso. Pedidos de acordo chegam ao chat, onde a equipe da loja negocia.",
        },
        {
          question: "Como funciona a baixa automática da parcela?",
          answer:
            "Pelo Fidexa Pay. A mensagem de cobrança já traz a opção de pagar: o cliente seleciona a parcela, quita por Pix no próprio WhatsApp e a Fidexa registra a baixa. Antes do Fidexa Pay, a loja ainda tinha de conferir o Pix no extrato e baixar a parcela no ERP por conta própria.",
        },
        {
          question: "O que é o Fidexa Pay e qual a relação com a TOTVS Linx?",
          answer:
            "Lançado em agosto de 2026, o Fidexa Pay é a parte da plataforma que recebe o Pix e baixa as parcelas. A PEGN conta que ele é fruto de uma parceria com a Totvs Linx, e a primeira versão foi pensada para quem já opera no ERP da TOTVS Linx.",
        },
        {
          question: "Preciso trocar meu sistema de gestão para usar a Fidexa?",
          answer:
            "Não. O Linx segue como o sistema da loja. A Fidexa entra só na ponta da cobrança e do recebimento das parcelas do crediário próprio.",
        },
        {
          question: "A Fidexa cobra parcelas que ainda não venceram?",
          answer:
            "Sim. A régua manda o aviso antes do vencimento e outro no dia, e o cliente já pode quitar a parcela futura por Pix na mesma mensagem. As atrasadas seguem na régua depois do vencimento.",
        },
        {
          question: "Minha rede tem várias lojas no Linx. Funciona?",
          answer:
            "Sim. O lojista centraliza a carteira de várias unidades num dashboard único e vê, num lugar só, os débitos pendentes de cada loja.",
        },
        {
          question: "Uso outro ERP. A Fidexa funciona mesmo assim?",
          answer:
            "Sim. Além do Linx, a Fidexa se conecta por API a outros sistemas listados na página de integrações. Em Outros ERPs, basta exportar uma planilha da carteira, que a Fidexa importa. “O próximo passo é crescer dentro dessa base, e atuar com outros ERPs”, disse André Menezes, sócio da Fidexa, à PEGN.",
        },
        {
          question: "A cobrança no WhatsApp segue as regras da Meta?",
          answer:
            "Sim. Os textos seguem o modelo de template exigido pela Meta e são enviados pela API oficial. A Fidexa tem o selo de autenticação da Meta, como contou o fundador Bruno Reis à PEGN.",
        },
        {
          question: "Quanto custa a integração com o Linx?",
          answer:
            "O valor depende do tamanho da operação, e a integração entra nessa conta. Na demonstração gratuita, um especialista conecta o cenário do seu Linx e traz a proposta.",
        },
      ],
    },
    {
      type: "finalCta",
      id: "cta-final",
      title: "Ligue o Linx da sua loja à cobrança no WhatsApp",
      text: "Um especialista mostra, na demonstração gratuita, a carteira do seu ERP virando cobrança e Pix baixado.",
      ctaLabel: "Agendar demonstração gratuita",
    },
  ],
};
